import { blockUSB, formatNonOSDrives, formatDrive, deleteFileFolder, encryptFileFolder, decryptFileFolder, formatDeleteFile, formatEncryptFile, formatDecryptFile } from './agent';
import { addOperation, addQueueOperation, editLastLogin, editUsbStatus } from './axios';

// Helper function to check device status (same as in DeviceTable.js)
const checkDeviceStatus = async (device) => {
  try {
    if (!device.id) {
      throw new Error('Device ID is missing.');
    }
    if (!device.device_uuid) {
      throw new Error('Device UUID is missing.');
    }
    if (!device.sys_uuid) {
      throw new Error('System UUID is missing.');
    }

    const device_id = device.id;
    const apiResponse = await editLastLogin({ id: device_id, uuid: device.device_uuid }, device.sys_uuid);
    const isActive = apiResponse.data.isActive === 1;
    return { isActive, device_id };
  } catch (error) {
    console.error(`Error checking device status for UUID ${device.device_uuid}:`, error.message);
    return { isActive: false, device_id: device.id };
  }
};

// Retry function for usb_blocking
export const retryUsbBlocking = async (log, user_id) => {
  const { device_id, device_uuid, sys_uuid } = log;
  const action = 'block';

  const { isActive, device_id: deviceId } = await checkDeviceStatus({ id: device_id, device_uuid, sys_uuid });
  let operationLogId;

  if (isActive) {
    const operationResponse = await addOperation({
      device_id: deviceId,
      type: 'usb_blocking',
      user_id,
    });
    operationLogId = operationResponse.data.log_id;
    await blockUSB(device_uuid, action, operationLogId, sys_uuid);
    await editUsbStatus({
      id: device_id,
      status: action,
    });
  } else {
    const queueResponse = await addQueueOperation({
      device_id: deviceId,
      type: 'usb_blocking',
      user_id,
    });
    operationLogId = queueResponse.data.log_id;
  }

  return { success: true, log_id: operationLogId };
};

// Retry function for usb_unblock
export const retryUsbUnblock = async (log, user_id) => {
  const { device_id, device_uuid, sys_uuid } = log;
  const action = 'unblock';

  const { isActive, device_id: deviceId } = await checkDeviceStatus({ id: device_id, device_uuid, sys_uuid });
  let operationLogId;

  if (isActive) {
    const operationResponse = await addOperation({
      device_id: deviceId,
      type: 'usb_unblock',
      user_id,
    });
    operationLogId = operationResponse.data.log_id;
    await blockUSB(device_uuid, action, operationLogId, sys_uuid);
    await editUsbStatus({
      id: device_id,
      status: action,
    });
  } else {
    const queueResponse = await addQueueOperation({
      device_id: deviceId,
      type: 'usb_unblock',
      user_id,
    });
    operationLogId = queueResponse.data.log_id;
  }

  return { success: true, log_id: operationLogId };
};

// Retry function for delete_drive
export const retryDeleteDrive = async (log, user_id) => {
  const { device_id, device_uuid, sys_uuid } = log;

  const { isActive, device_id: deviceId } = await checkDeviceStatus({ id: device_id, device_uuid, sys_uuid });
  let operationLogId;

  if (isActive) {
    const operationResponse = await addOperation({
      device_id: deviceId,
      type: 'delete_drive',
      user_id,
    });
    operationLogId = operationResponse.data.log_id;
    await formatNonOSDrives(device_uuid, operationLogId, sys_uuid);
  } else {
    const queueResponse = await addQueueOperation({
      device_id: deviceId,
      type: 'delete_drive',
      user_id,
    });
    operationLogId = queueResponse.data.log_id;
  }

  return { success: true, log_id: operationLogId };
};

// Retry function for format_drive
export const retryFormatDrive = async (log, user_id) => {
  const { device_id, device_uuid, sys_uuid, json } = log;

  let driveLetter;
  try {
    const parsedJson = JSON.parse(json);
    if (!parsedJson.path) {
      throw new Error('Drive path is missing in log JSON.');
    }
    driveLetter = parsedJson.path.replace(/\\+/g, '').replace(/:/g, '').trim();
    if (!driveLetter.endsWith(':')) {
      driveLetter += ':';
    }
  } catch (error) {
    console.error('Error parsing drive letter from log JSON:', error.message);
    throw new Error('Failed to parse drive letter from log.');
  }

  const { isActive, device_id: deviceId } = await checkDeviceStatus({ id: device_id, device_uuid, sys_uuid });
  let operationLogId;

  if (isActive) {
    const operationResponse = await addOperation({
      device_id: deviceId,
      type: 'format_drive',
      user_id,
      json: { path: driveLetter },
    });
    operationLogId = operationResponse.data.log_id;
    await formatDrive(device_uuid, driveLetter, operationLogId, sys_uuid);
  } else {
    const queueResponse = await addQueueOperation({
      device_id: deviceId,
      type: 'format_drive',
      user_id,
      json: { path: driveLetter },
    });
    operationLogId = queueResponse.data.log_id;
  }

  return { success: true, log_id: operationLogId };
};

// Retry function for delete_file/folder
export const retryDeleteFileFolder = async (log, user_id) => {
  const { device_id, device_uuid, sys_uuid, json } = log;

  let path;
  try {
    const parsedJson = JSON.parse(json);
    if (!parsedJson.path) {
      throw new Error('File/Folder path is missing in log JSON.');
    }
    path = parsedJson.path;
  } catch (error) {
    console.error('Error parsing path from log JSON:', error.message);
    throw new Error('Failed to parse path from log.');
  }

  const { isActive, device_id: deviceId } = await checkDeviceStatus({ id: device_id, device_uuid, sys_uuid });
  let operationLogId;

  if (isActive) {
    const operationResponse = await addOperation({
      device_id: deviceId,
      type: 'delete_file/folder',
      user_id,
      json: { path },
    });
    operationLogId = operationResponse.data.log_id;
    await deleteFileFolder(device_uuid, path, operationLogId, sys_uuid);
  } else {
    const queueResponse = await addQueueOperation({
      device_id: deviceId,
      type: 'delete_file/folder',
      user_id,
      json: { path },
    });
    operationLogId = queueResponse.data.log_id;
  }

  return { success: true, log_id: operationLogId };
};

// Retry function for encryption (requires password)
export const retryEncryptFileFolder = async (log, user_id, password) => {
  const { device_id, device_uuid, sys_uuid, json } = log;
  let path;
  try {
    const parsedJson = JSON.parse(json);
    if (!parsedJson.path) {
      throw new Error('File/Folder path is missing in log JSON.');
    }
    path = parsedJson.path;
  } catch (error) {
    console.error('Error parsing path from log JSON:', error.message);
    throw new Error('Failed to parse path from log.');
  }

  if (!password) {
    throw new Error('Password is required for encryption retry.');
  }

  const { isActive, device_id: deviceId } = await checkDeviceStatus({ id: device_id, device_uuid, sys_uuid });
  let operationLogId;

  if (isActive) {
    const operationResponse = await addOperation({
      device_id: deviceId,
      type: 'encryption',
      user_id,
      json: { path, password },
    });
    operationLogId = operationResponse.data.log_id;
    await encryptFileFolder(device_uuid, path, password, operationLogId, sys_uuid);
  } else {
    const queueResponse = await addQueueOperation({
      device_id: deviceId,
      type: 'encryption',
      user_id,
      json: { path, password },
    });
    operationLogId = queueResponse.data.log_id;
  }

  return { success: true, log_id: operationLogId };
};

// Retry function for decryption (requires password)
export const retryDecryptFileFolder = async (log, user_id, password) => {
  const { device_id, device_uuid, sys_uuid, json } = log;

  let path;
  try {
    const parsedJson = JSON.parse(json);
    if (!parsedJson.path) {
      throw new Error('File/Folder path is missing in log JSON.');
    }
    path = parsedJson.path;
  } catch (error) {
    console.error('Error parsing path from log JSON:', error.message);
    throw new Error('Failed to parse path from log.');
  }

  if (!password) {
    throw new Error('Password is required for decryption retry.');
  }

  const { isActive, device_id: deviceId } = await checkDeviceStatus({ id: device_id, device_uuid, sys_uuid });
  let operationLogId;

  if (isActive) {
    const operationResponse = await addOperation({
      device_id: deviceId,
      type: 'decryption',
      user_id,
      json: { path, password },
    });
    operationLogId = operationResponse.data.log_id;
    await decryptFileFolder(device_uuid, path, password, operationLogId, sys_uuid);
  } else {
    const queueResponse = await addQueueOperation({
      device_id: deviceId,
      type: 'decryption',
      user_id,
      json: { path, password },
    });
    operationLogId = queueResponse.data.log_id;
  }

  return { success: true, log_id: operationLogId };
};

// Retry function for delete_file_format
export const retryDeleteFileFormat = async (log, user_id) => {
  const { device_id, device_uuid, sys_uuid, json } = log;

  let path, format;
  try {
    const parsedJson = JSON.parse(json);
    if (!parsedJson.path) {
      throw new Error('File path is missing in log JSON.');
    }
    if (!parsedJson.format) {
      throw new Error('File format is missing in log JSON.');
    }
    path = parsedJson.path;
    format = parsedJson.format;
  } catch (error) {
    console.error('Error parsing path or format from log JSON:', error.message);
    throw new Error('Failed to parse path or format from log.');
  }

  const { isActive, device_id: deviceId } = await checkDeviceStatus({ id: device_id, device_uuid, sys_uuid });
  let operationLogId;

  if (isActive) {
    const operationResponse = await addOperation({
      device_id: deviceId,
      type: 'delete_file_format',
      user_id,
      json: { path, format },
    });
    operationLogId = operationResponse.data.log_id;
    await formatDeleteFile(device_uuid, path, format, operationLogId, sys_uuid);
  } else {
    const queueResponse = await addQueueOperation({
      device_id: deviceId,
      type: 'delete_file_format',
      user_id,
      json: { path, format },
    });
    operationLogId = queueResponse.data.log_id;
  }

  return { success: true, log_id: operationLogId };
};

// Retry function for encrypt_file_format (requires password)
export const retryEncryptFileFormat = async (log, user_id, password) => {
  const { device_id, device_uuid, sys_uuid, json } = log;

  let path, format;
  try {
    const parsedJson = JSON.parse(json);
    if (!parsedJson.path) {
      throw new Error('File path is missing in log JSON.');
    }
    if (!parsedJson.format) {
      throw new Error('File format is missing in log JSON.');
    }
    path = parsedJson.path;
    format = parsedJson.format;
  } catch (error) {
    console.error('Error parsing path or format from log JSON:', error.message);
    throw new Error('Failed to parse path or format from log.');
  }

  if (!password) {
    throw new Error('Password is required for encryption retry.');
  }

  const { isActive, device_id: deviceId } = await checkDeviceStatus({ id: device_id, device_uuid, sys_uuid });
  let operationLogId;

  if (isActive) {
    const operationResponse = await addOperation({
      device_id: deviceId,
      type: 'encrypt_file_format',
      user_id,
      json: { path, format, password },
    });
    operationLogId = operationResponse.data.log_id;
    await formatEncryptFile(device_uuid, path, format, password, operationLogId, sys_uuid);
  } else {
    const queueResponse = await addQueueOperation({
      device_id: deviceId,
      type: 'encrypt_file_format',
      user_id,
      json: { path, format, password },
    });
    operationLogId = queueResponse.data.log_id;
  }

  return { success: true, log_id: operationLogId };
};

// Retry function for decrypt_file_format (requires password)
export const retryDecryptFileFormat = async (log, user_id, password) => {
  const { device_id, device_uuid, sys_uuid, json } = log;

  let path, format;
  try {
    const parsedJson = JSON.parse(json);
    if (!parsedJson.path) {
      throw new Error('File path is missing in log JSON.');
    }
    if (!parsedJson.format) {
      throw new Error('File format is missing in log JSON.');
    }
    path = parsedJson.path;
    format = parsedJson.format;
  } catch (error) {
    console.error('Error parsing path or format from log JSON:', error.message);
    throw new Error('Failed to parse path or format from log.');
  }

  if (!password) {
    throw new Error('Password is required for decryption retry.');
  }

  const { isActive, device_id: deviceId } = await checkDeviceStatus({ id: device_id, device_uuid, sys_uuid });
  let operationLogId;

  if (isActive) {
    const operationResponse = await addOperation({
      device_id: deviceId,
      type: 'decrypt_file_format',
      user_id,
      json: { path, format, password },
    });
    operationLogId = operationResponse.data.log_id;
    await formatDecryptFile(device_uuid, path, format, password, operationLogId, sys_uuid);
  } else {
    const queueResponse = await addQueueOperation({
      device_id: deviceId,
      type: 'decrypt_file_format',
      user_id,
      json: { path, format, password },
    });
    operationLogId = queueResponse.data.log_id;
  }

  return { success: true, log_id: operationLogId };
};